import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, Search, CheckCircle, ExternalLink } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import type { AlertSeverity, AlertCategory } from "@/data/alertsEngine";
import { generateUnifiedAlerts } from "@/data/alertFeed";
import { useSafetyControl } from "@/data/SafetyControlContext";
import { ActionQueueService, type ActionQueueRow, type ActionQueuePayload } from "@/services/action-queue.service";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";

const SEVERITY_STYLES: Record<AlertSeverity, string> = {
  critical: "bg-destructive/10 text-destructive border-destructive/20",
  warning: "bg-warning/10 text-warning border-warning/20",
  info: "bg-muted text-muted-foreground border-border",
};

const CATEGORY_LABELS: Record<AlertCategory, string> = {
  job: "Job",
  qa: "QA",
  issue: "Issue",
  credit: "Credit",
  contractor: "Contractor",
  procurement: "Procurement",
  pricing: "Pricing",
  integrity: "System Integrity",
};

const ITEMS_PER_PAGE = 20;

export default function AlertsHistory() {
  const { jobs, issues, contractors, opportunities, credits, quotes } = useAppData();
  const { rules } = useSafetyControl();
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState<"all" | AlertSeverity>("all");
  const [categoryFilter, setCategoryFilter] = useState<"all" | AlertCategory>("all");
  const [page, setPage] = useState(1);

  // ── Resolved ECC items from Supabase ──
  const [resolvedItems, setResolvedItems] = useState<ActionQueueRow[]>([]);
  const [resolvedSearch, setResolvedSearch] = useState("");
  const [resolvedPage, setResolvedPage] = useState(1);
  const [resolvedLoading, setResolvedLoading] = useState(true);
  const [resolvedError, setResolvedError] = useState<string | null>(null);

  useEffect(() => {
    setResolvedLoading(true);
    setResolvedError(null);
    ActionQueueService.getResolved().then(({ data, error }) => {
      if (error) {
        console.error("Failed to load resolved items:", error);
        setResolvedError(String(error));
      }
      setResolvedItems(data ?? []);
      setResolvedLoading(false);
    });
  }, []);

  const alerts = useMemo(
    () => generateUnifiedAlerts(jobs, issues, contractors, opportunities, credits, quotes, rules),
    [jobs, issues, contractors, opportunities, credits, quotes, rules]
  );

  const filtered = useMemo(() => {
    let result = alerts;
    if (severityFilter !== "all") result = result.filter(a => a.severity === severityFilter);
    if (categoryFilter !== "all") result = result.filter(a => a.category === categoryFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [alerts, severityFilter, categoryFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paged = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const counts = useMemo(() => ({
    critical: alerts.filter(a => a.severity === "critical").length,
    warning: alerts.filter(a => a.severity === "warning").length,
    info: alerts.filter(a => a.severity === "info").length,
    total: alerts.length,
  }), [alerts]);

  // ── Resolved items filtering ──
  const filteredResolved = useMemo(() => {
    if (!resolvedSearch.trim()) return resolvedItems;
    const q = resolvedSearch.toLowerCase();
    return resolvedItems.filter(r => {
      const p = r.payload as ActionQueuePayload;
      return (
        (p.headline ?? "").toLowerCase().includes(q) ||
        (p.context ?? "").toLowerCase().includes(q) ||
        (p.job_id ?? "").toLowerCase().includes(q)
      );
    });
  }, [resolvedItems, resolvedSearch]);

  const resolvedTotalPages = Math.max(1, Math.ceil(filteredResolved.length / ITEMS_PER_PAGE));
  const pagedResolved = filteredResolved.slice((resolvedPage - 1) * ITEMS_PER_PAGE, resolvedPage * ITEMS_PER_PAGE);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Bell className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold text-foreground">Alerts History</h1>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {([
          { label: "Total", value: counts.total, style: "text-foreground" },
          { label: "Critical", value: counts.critical, style: "text-destructive" },
          { label: "Warning", value: counts.warning, style: "text-warning" },
          { label: "Resolved", value: resolvedItems.length, loading: resolvedLoading, style: "text-success" },
        ]).map(s => (
          <Card key={s.label} className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</div>
            {"loading" in s && s.loading ? (
              <Skeleton className="h-8 w-12 mt-1" />
            ) : (
              <div className={cn("text-2xl font-bold mt-1", s.style)}>{s.value}</div>
            )}
          </Card>
        ))}
      </div>

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" className="text-xs gap-1.5">
            <Bell className="h-3 w-3" /> Active Alerts
            {counts.total > 0 && (
              <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive">{counts.total}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="resolved" className="text-xs gap-1.5">
            <CheckCircle className="h-3 w-3" /> Resolved
            {resolvedItems.length > 0 && (
              <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-success/10 text-success">{resolvedItems.length}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Active Alerts ── */}
        <TabsContent value="active" className="space-y-3 mt-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search alerts…"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <Select value={severityFilter} onValueChange={v => { setSeverityFilter(v as any); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-[140px] h-9 text-sm">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={v => { setCategoryFilter(v as any); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-[160px] h-9 text-sm">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">
                {filtered.length} alert{filtered.length !== 1 ? "s" : ""} found
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {paged.length === 0 ? (
                <div className="px-4 py-12">
                  <EmptyState
                    icon={Bell}
                    title="No alerts match your filters"
                    description="Try adjusting your search or filter criteria"
                  />
                </div>
              ) : (
                <div className="divide-y">
                  {paged.map(alert => (
                    <Link
                      key={alert.id}
                      to={alert.actionLink}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors"
                    >
                      <div className="pt-1 shrink-0">
                        <div className={cn(
                          "h-2.5 w-2.5 rounded-full",
                          alert.severity === "critical" ? "bg-destructive" :
                          alert.severity === "warning" ? "bg-warning" : "bg-muted-foreground/40"
                        )} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-foreground">{alert.title}</span>
                          <span className={cn(
                            "inline-flex items-center rounded border px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider",
                            SEVERITY_STYLES[alert.severity]
                          )}>
                            {alert.severity}
                          </span>
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 capitalize">
                            {CATEGORY_LABELS[alert.category]}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {alert.description}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-[10px] text-primary font-medium flex items-center gap-1">
                            {alert.actionLabel} <ExternalLink className="h-2.5 w-2.5" />
                          </span>
                          <span className="text-[10px] text-muted-foreground/60">
                            {new Date(alert.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-xs font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed">← Previous</button>
                  <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed">Next →</button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Resolved Items ── */}
        <TabsContent value="resolved" className="space-y-3 mt-3">
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search resolved items…"
                  value={resolvedSearch}
                  onChange={e => { setResolvedSearch(e.target.value); setResolvedPage(1); }}
                  className="pl-9 h-9 text-sm"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">
                {resolvedLoading ? "Loading…" : `${filteredResolved.length} resolved item${filteredResolved.length !== 1 ? "s" : ""}`}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {resolvedLoading ? (
                <div className="divide-y">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-3">
                      <div className="pt-1 shrink-0">
                        <Skeleton className="h-3.5 w-3.5 rounded-full" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-4 w-16 rounded" />
                        </div>
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : resolvedError ? (
                <div className="flex flex-col items-center py-12 text-destructive">
                  <p className="text-sm">Failed to load resolved items</p>
                  <p className="text-xs mt-1 text-muted-foreground">{resolvedError}</p>
                </div>
               ) : pagedResolved.length === 0 ? (
                 <div className="px-4 py-12">
                   <EmptyState
                     icon={CheckCircle}
                     title="No resolved items yet"
                     description="Items marked complete in the Executive Command Center will appear here"
                   />
                 </div>
               ) : (
                <div className="divide-y">
                  {pagedResolved.map(row => {
                    const p = row.payload as ActionQueuePayload;
                    const resolvedAt = new Date(row.updated_at).toLocaleDateString("en-US", {
                      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
                    });
                    return (
                      <div key={row.id} className="flex items-start gap-3 px-4 py-3">
                        <div className="pt-1 shrink-0">
                          <CheckCircle className="h-3.5 w-3.5 text-success" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-foreground">{p.headline}</span>
                            <span className="inline-flex items-center rounded border px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider bg-success/10 text-success border-success/20">
                              Resolved
                            </span>
                            {p.job_id && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0">{p.job_id}</Badge>
                            )}
                          </div>
                          {p.context && (
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{p.context}</p>
                          )}
                          {p.why_it_matters && (
                            <p className="text-[11px] text-muted-foreground/70 mt-0.5 italic">{p.why_it_matters}</p>
                          )}
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-[10px] text-muted-foreground/60">Resolved {resolvedAt}</span>
                            {p.job_id && (
                              <Link to={`/jobs/${p.job_id}`} className="text-[10px] text-primary font-medium flex items-center gap-1 hover:underline">
                                View Job <ExternalLink className="h-2.5 w-2.5" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {resolvedTotalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <button disabled={resolvedPage <= 1} onClick={() => setResolvedPage(p => p - 1)} className="text-xs font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed">← Previous</button>
                  <span className="text-xs text-muted-foreground">Page {resolvedPage} of {resolvedTotalPages}</span>
                  <button disabled={resolvedPage >= resolvedTotalPages} onClick={() => setResolvedPage(p => p + 1)} className="text-xs font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed">Next →</button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
